/* eslint-disable camelcase */
import { getMessaging, Messaging, onMessage, MessagePayload } from "firebase/messaging";
import { useEffect, useMemo, useRef } from "react";
import { getApps, initializeApp } from "firebase/app";
import { firebaseConfig } from "../../../api/firebase-config";
import SnackbarUtils from "../../../utils/SnackbarUtils";
import { Routes } from "../../../routes-config";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { selectCallStatus } from "../../shared/slices/voipSlice";

interface MessageEventData {
  type: "notificationReceived" | "backgroundMessage" | "notificationClick" | "notificationSound";
  payload: MessagePayload;
  present?: boolean;
  receivedAt?: number;
  expiresAt?: number;
  soundDelegated?: boolean;
  compatibilitySync?: boolean;
}

interface NotificationData {
  type?: string;
  notification_id?: string;
  unconfirmed_call_id?: string;
}

const getFirebaseApp = () => {
  return getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
};

const playNotificationSound = (ctx: AudioContext | undefined) => {
  if (!ctx || ctx.state !== "running") return false;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 1200;
    osc.type = "square";
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.setValueAtTime(0.25, ctx.currentTime + 0.01);
    gain.gain.setValueAtTime(0, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.25, ctx.currentTime + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
    return true;
  } catch (error) {
    console.error("Failed to play notification sound:", error);
    return false;
  }
};

const showNewMessageNotification = () => {
  if (!window.location.pathname.includes(Routes.COMMUNICATION)) {
    SnackbarUtils.success("New chat message received!");
  }
};

export const useFirebaseMessaging = ({
  onFirebaseMessage,
}: {
  onFirebaseMessage: (
    id: string | undefined,
    type: string,
    isOnCall?: boolean,
    present?: boolean,
  ) => void;
}): { firebaseMessaging: Messaging } => {
  const firebaseApp = useMemo(() => getFirebaseApp(), []);
  const firebaseMessaging = useMemo(() => getMessaging(firebaseApp), [firebaseApp]);
  const callStatus = useAppSelector(selectCallStatus);

  const callStatusRef = useRef(callStatus);
  callStatusRef.current = callStatus;

  const onFirebaseMessageRef = useRef(onFirebaseMessage);
  onFirebaseMessageRef.current = onFirebaseMessage;

  useEffect(() => {
    let audioContext: AudioContext | undefined;
    let disposed = false;
    const presentedIds = new Set<string>();
    const activateAudio = () => {
      try {
        audioContext ??= new AudioContext();
        if (audioContext.state === "suspended") {
          audioContext
            .resume()
            .catch((error) => console.error("Failed to activate notification sound:", error));
        }
      } catch (error) {
        console.error("Failed to activate notification sound:", error);
      }
    };
    // Never queue a beep on a suspended context: it would replay on a later user gesture.
    const handlePayload = (
      payload: MessagePayload,
      present: boolean,
      clicked = false,
      sound = true,
    ) => {
      if (present && payload.messageId) {
        if (presentedIds.has(payload.messageId)) present = false;
        else {
          presentedIds.add(payload.messageId);
          if (presentedIds.size > 100)
            presentedIds.delete(presentedIds.values().next().value as string);
        }
      }
      if (present && sound) playNotificationSound(audioContext);
      const data = payload.data as NotificationData | undefined;
      const type = data?.type;
      if (type === "unconfirmed_calls" && data?.unconfirmed_call_id) {
        onFirebaseMessageRef.current(
          data.unconfirmed_call_id,
          type,
          callStatusRef.current.isOnCall,
          clicked || (present && document.visibilityState === "visible"),
        );
      } else if (data?.notification_id) {
        onFirebaseMessageRef.current(
          data.notification_id,
          type || "backgroundMessage",
          undefined,
          present && document.visibilityState === "visible",
        );
      } else if (
        !type &&
        !data?.unconfirmed_call_id &&
        present &&
        document.visibilityState === "visible"
      ) {
        showNewMessageNotification();
      }
    };

    const handleBackgroundMessage = (event: MessageEvent<MessageEventData>) => {
      if (event.data?.type === "notificationSound") {
        const fresh = event.data.expiresAt !== undefined && Date.now() < event.data.expiresAt;
        event.ports[0]?.postMessage(fresh && playNotificationSound(audioContext));
        event.ports[0]?.close();
        return;
      }
      if (
        !["notificationReceived", "backgroundMessage", "notificationClick"].includes(
          event.data?.type,
        ) ||
        !event.data.payload
      )
        return;
      const { payload, type, receivedAt } = event.data;
      // A frozen current page can receive the legacy fallback after its probe times out.
      if (event.data.compatibilitySync) {
        handlePayload(payload, false);
        return;
      }
      // ponytail: discard sounds delayed over 5s; use push timestamps if delivery-age tracking is needed.
      const fresh = receivedAt !== undefined && Date.now() - receivedAt < 5000;
      handlePayload(
        payload,
        type !== "notificationClick" && event.data.present === true && fresh,
        type === "notificationClick",
        !event.data.soundDelegated,
      );
    };

    const unsubscribe = onMessage(firebaseMessaging, async (payload) => {
      try {
        const registration = await navigator.serviceWorker.getRegistration(
          "/firebase-messaging-sw.js",
        );
        if (registration?.active && payload.messageId) {
          const activeWorker = registration.active;
          const accepted = await new Promise<boolean>((resolve) => {
            const channel = new MessageChannel();
            const timer = setTimeout(() => {
              channel.port1.close();
              resolve(false);
            }, 500);
            channel.port1.onmessage = () => {
              clearTimeout(timer);
              channel.port1.close();
              resolve(true);
            };
            activeWorker.postMessage(
              { type: "notificationForeground", payload, expiresAt: Date.now() + 400 },
              [channel.port2],
            );
          });
          if (accepted) return;
        }
      } catch (error) {
        console.error("Failed to route foreground notification:", error);
      }
      // An older worker may still be active during deployment. Keep foreground delivery working.
      if (!disposed) handlePayload(payload, document.visibilityState === "visible");
    });
    window.addEventListener("pointerdown", activateAudio);
    window.addEventListener("keydown", activateAudio);
    if (navigator.userActivation?.hasBeenActive) activateAudio();

    navigator.serviceWorker.addEventListener("message", handleBackgroundMessage);

    return () => {
      disposed = true;
      unsubscribe();
      window.removeEventListener("pointerdown", activateAudio);
      window.removeEventListener("keydown", activateAudio);
      if (audioContext) audioContext.close().catch(() => undefined);
      navigator.serviceWorker.removeEventListener("message", handleBackgroundMessage);
    };
  }, [firebaseMessaging]);

  return { firebaseMessaging };
};
